import { BookOpenTextIcon, CalendarCheckIcon, CalendarXIcon, ClipboardCheckIcon, DicesIcon, FileTextIcon, FolderSearchIcon, LayoutGridIcon, LightbulbIcon, PencilRulerIcon, ShapesIcon, ShuffleIcon } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
// Caja 3D transparente de los 6 dílů (Recursos de la tienda VÝTVARKA HOTOVÁ).
export const PRODUCT_IMAGE = 'https://aekpgsbqjzqntlbucbts.supabase.co/storage/v1/object/public/store-resources/9149c32d-950e-46a4-9f6d-96ee13602bda/1793765991189_vytvarka-caja-6-dilu-transparente-b8846cc5.webp';
export const heroGuarantees = ['Okamžitě ke stažení', 'Jednorázová platba', 'Žádné předplatné', '7denní záruka spokojenosti'];
export const stats = [{
  value: '240',
  label: 'Stran v šesti dílech'
}, {
  value: '6',
  label: 'Digitálních dílů výtvarné výchovy'
}, {
  value: '3',
  label: 'Skupiny ročníků – od 1. do 9. ročníku'
}, {
  value: '5',
  label: 'Bonusů v ceně'
}, {
  value: '1',
  label: 'Kompletní systém pro hodiny výtvarné výchovy'
}];
export const differenceStats = [{
  value: '1.–9. ročník',
  label: '1. i 2. stupeň ZŠ, s pracovními listy ve 3 úrovních'
}, {
  value: '6 digitálních dílů',
  label: 'Od ročního plánu po závěrečnou výstavu v červnu'
}, {
  value: 'PDF k tisku',
  label: 'K promítnutí na tabuli nebo k vytištění pro třídu'
}];
export const beforeItems: Array<{
  title: string;
  text: string;
}> = [{
  title: 'Přijdete o nedělní večer',
  text: 'Hodiny u počítače nad přípravou hodin na celý týden – a pořád to není hotové.'
}, {
  title: 'Hodiny hledání',
  text: 'Nápady z internetu jsou moc složité, potřebují moc pomůcek nebo neodpovídají RVP ZV.'
}, {
  title: 'Každá hodina od nuly',
  text: 'Motivace, zadání, pomůcky, průběh, reflexe – co by mělo zabrat pár minut, se mění v samostatnou práci.'
}, {
  title: 'Různé úrovně v jedné třídě',
  text: 'Žák, který se bojí začít, a žák, který má hotovo za deset minut, v jedné třídě: někdo se vždycky ztratí.'
}, {
  title: 'Hodnocení zůstává nejasné',
  text: 'Jak hodnotit tvořivou práci? Blíží se vysvědčení a vy potřebujete záznamy a ta správná slova.'
}, {
  title: 'Stres před hodinou',
  text: 'Když chybí čas, na poslední chvíli improvizujete a rozdáte omalovánky.'
}];
export const afterItems = ['Přehledný balíček výtvarné výchovy od 1. do 9. ročníku', 'Hodiny minutu po minutě, snadno k použití', 'Techniky vysvětlené krok za krokem', 'Pracovní listy ve 3 úrovních', 'Dějiny umění a 40 představených umělců', 'Prostorová tvorba, fotografie a digitál v projektech', 'Hodnoticí tabulky podle očekávaných výstupů', 'Závěrečná výstava prací krok za krokem', 'Plán na celý školní rok'];
export const recognizeItems: Array<{
  icon: LucideIcon;
  title: string;
  text: string;
}> = [{
  icon: CalendarXIcon,
  title: 'Víkendy plné přípravy',
  text: 'Trávíte část víkendu přípravou hodin na další týden?'
}, {
  icon: FolderSearchIcon,
  title: 'Nevíte, co dětem zadat',
  text: 'Pořád hledáte nápady na hodiny, které odpovídají úrovni vašich žáků?'
}, {
  icon: LightbulbIcon,
  title: 'Chcete vysvětlovat srozumitelněji',
  text: 'Potřebujete názorné podklady, abyste techniku ukázali jednoduše, krok za krokem?'
}, {
  icon: PencilRulerIcon,
  title: 'Chcete přestat všechno vytvářet od nuly',
  text: 'Chcete mít hodiny, pracovní listy a hodnoticí tabulky připravené předem?'
}, {
  icon: ShuffleIcon,
  title: 'Vaši žáci potřebují pestrost',
  text: 'Chcete střídat techniky – malbu, prostorovou tvorbu, fotografii, koláž – a netrávit nad tím večery?'
}, {
  icon: LayoutGridIcon,
  title: 'Potřebujete jasnou strukturu',
  text: 'Chcete mít všechen materiál přehledně pohromadě a rychle najít, co potřebujete?'
}];

// Spine colours mirror the physical box (blue, light blue, green, yellow, orange, red).
export const volumes: Array<{
  number: string;
  level: string;
  title: string;
  tagline: string;
  intro: string;
  items: string[];
  outro: string;
  note?: string;
  spine: string;
  rule: string;
}> = [{
  number: 'Díl 01',
  level: '1.–9. ročník',
  title: 'Roční plán a 12 hotových hodin',
  tagline: 'Celý školní rok zorganizovaný, blok po bloku.',
  intro: 'První díl obsahuje roční plán pro tři skupiny ročníků a 12 kompletních hodin minutu po minutě.',
  items: ['Roční plán pro 1.–3., 4.–6. a 7.–9. ročník', '4 oblasti výtvarné práce podle RVP ZV', 'Stavba hodiny na 45 minut i dvouhodinovky', '3 úrovně v jedné třídě', '12 hodin minutu po minutě', 'Pracovní listy k tisku', 'Uspořádání učebny a pomůcek', 'Bezpečnost a kontrolní seznam', 'Přehled výstupů třídy'],
  outro: 'Vstoupíte do třídy a přesně víte, co budete dělat.',
  spine: 'bg-blue-700 text-white',
  rule: 'border-white/30'
}, {
  number: 'Díl 02',
  level: '1.–9. ročník',
  title: 'Kresba, barva a malba',
  tagline: 'Každá technika jednoduše vysvětlená.',
  intro: 'Druhý díl učí kresbu, barvu a malbu – se stránkami o technikách a pracovními listy ve 3 úrovních.',
  items: ['Kresba podle skutečnosti', 'Obličej, postava a proporce', 'Plány a perspektiva', 'Barevný kruh a kontrasty', 'Odstíny a přechody', 'Tempery, vodovky, pastely a uhel', 'Vzor a opakování', 'Ilustrace a komiks'],
  outro: 'Ukázat, vytisknout, tvořit.',
  spine: 'bg-sky-500 text-slate-900',
  rule: 'border-slate-900/20'
}, {
  number: 'Díl 03',
  level: '1.–9. ročník',
  title: 'Prostor, materiály a tvoření',
  tagline: 'Prostorové projekty bez stresu.',
  intro: 'Třetí díl obsahuje 12 prostorových projektů krok za krokem, se spočítanými pomůckami, bezpečností a pracovními listy.',
  items: ['Pohyby při modelování', 'Spojování: lepit, vázat, zasouvat', 'Masky, roboti a totemy', 'Tkaní a otisky', 'Tisk z polystyrenu', 'Land art na školním dvoře', 'Architektura z kartonu', 'Podstavce a prezentace'],
  outro: 'Prostorová tvorba konečně pro celou třídu.',
  spine: 'bg-green-600 text-white',
  rule: 'border-white/30'
}, {
  number: 'Díl 04',
  level: '1.–9. ročník',
  title: 'Obraz, fotografie, digitál a design',
  tagline: 'Číst obrazy a tvořit je.',
  intro: 'Čtvrtý díl učí volit výřez, fotografovat, vytvořit plakát, animovat po políčkách a přemýšlet jako designér.',
  items: ['Záběry, výřezy a úhly pohledu', 'Světlo ve fotografii', 'Fotomontáž a plakát', 'Logo a písmo, které mluví', 'Animace po políčkách a pixel art', 'Pravý, nebo upravený obraz?', 'Design předmětu a architektura', 'Fotopříběh třídy'],
  outro: 'S tabletem i bez něj: každý projekt funguje.',
  spine: 'bg-yellow-400 text-slate-900',
  rule: 'border-slate-900/20'
}, {
  number: 'Díl 05',
  level: '1.–9. ročník',
  title: 'Dějiny umění a setkání s díly',
  tagline: 'Díla, ze kterých má člověk chuť tvořit.',
  intro: 'Pátý díl prochází dějinami umění od pravěku po současnost – ke každému období čtení díla a tvořivá dílna „ve stylu…“.',
  items: ['Jak číst dílo ve 4 krocích', 'Časová osa dějin umění', 'Od jeskynních maleb po pouliční umění', 'Monet, Van Gogh, Picasso, Matisse', 'Umění světa', 'Jak připravit návštěvu galerie', 'Setkání s uměním po celý rok', 'Obhajoba projektu v 9. ročníku'],
  outro: 'Umělecká kultura bez přípravy.',
  spine: 'bg-orange-500 text-slate-900',
  rule: 'border-slate-900/20'
}, {
  number: 'Díl 06',
  level: '1.–9. ročník',
  title: 'Hodnocení a projekty',
  tagline: 'Jasné hodnocení, viditelný pokrok.',
  intro: 'Šestý díl obsahuje hodnoticí tabulky ve 4 úrovních, 100 hotových slovních hodnocení, 6 projektů a závěrečnou výstavu prací.',
  items: ['Tabulky ve 4 úrovních', 'Sebehodnocení a vzájemné hodnocení', 'Skicák', '100 hotových slovních hodnocení', 'Třídní schůzky a konzultace s rodiči', '6 hotových projektů', 'Závěrečná výstava prací', 'Diplom mladého umělce'],
  outro: 'Viditelný pokrok pro žáky i rodiče.',
  note: 'Tento materiál není oficiální publikací MŠMT. Vychází z RVP ZV.',
  spine: 'bg-red-600 text-white',
  rule: 'border-white/30'
}];
export const volumesIntro = {
  title: '6 digitálních dílů od 1. do 9. ročníku',
  text: 'Kompletní balíček výtvarné výchovy, se kterým připravíte a odučíte hodiny celého školního roku. Každý díl má 40 stran s hodinami, technikami, pracovními listy a hodnoticími tabulkami pro třídu.'
};
export const packItems: Array<{
  icon: LucideIcon;
  title: string;
  text: string;
}> = [{
  icon: BookOpenTextIcon,
  title: 'Hotové hodiny',
  text: 'Žádný prázdný papír. Hodiny minutu po minutě i s motivací a slovy učitele – můžete hned začít.'
}, {
  icon: FileTextIcon,
  title: 'Pracovní listy k tisku',
  text: 'Pracovní listy ve 3 úrovních, plakáty a hodnoticí tabulky k vytištění nebo k promítnutí.'
}, {
  icon: ShapesIcon,
  title: 'Techniky v obrázcích',
  text: 'Ilustrované kroky, příklady a tipy, se kterými každou techniku ukážete jednoduše.'
}, {
  icon: DicesIcon,
  title: 'Projekty a dílny',
  text: 'Prostorová tvorba, fotografie, animace po políčkách, dílny „ve stylu…“: pestrost na celý rok.'
}, {
  icon: ClipboardCheckIcon,
  title: 'Hodnocení podle výstupů',
  text: 'Tabulky ve 4 úrovních, sebehodnocení a 100 hotových slovních hodnocení na vysvědčení.'
}, {
  icon: CalendarCheckIcon,
  title: 'Plán na celý školní rok',
  text: 'Od září po červnovou výstavu: každý blok má své téma, cíle a umělce.'
}];
export const pageSamples: Array<{src: string; label: string; title: string; text: string}> = [
  {
    "src": "https://aekpgsbqjzqntlbucbts.supabase.co/storage/v1/object/public/store-resources/9149c32d-950e-46a4-9f6d-96ee13602bda/1793775107551_vytvarka-dil01-p01-68de6d22.webp",
    "label": "Díl 01 · Strana 1",
    "title": "Obálka",
    "text": "Roční plán a 12 hotových hodin minutu po minutě."
  },
  {
    "src": "https://aekpgsbqjzqntlbucbts.supabase.co/storage/v1/object/public/store-resources/9149c32d-950e-46a4-9f6d-96ee13602bda/1793775123243_vytvarka-dil01-p10-6c107e18.webp",
    "label": "Hotová hodina · Strana 10",
    "title": "Hodina 1 · Kouzelné míchání – základní barvy",
    "text": "Průběh minutu po minutě s motivací, pomůckami a slovy učitele."
  },
  {
    "src": "https://aekpgsbqjzqntlbucbts.supabase.co/storage/v1/object/public/store-resources/9149c32d-950e-46a4-9f6d-96ee13602bda/1793775131133_vytvarka-dil01-p11-d5b5870c.webp",
    "label": "Pracovní list · Strana 11",
    "title": "Pracovní list ve 3 úrovních",
    "text": "Zadání, 3 úrovně, „Zvládám, když…“ a sebehodnocení na jedné stránce."
  },
  {
    "src": "https://aekpgsbqjzqntlbucbts.supabase.co/storage/v1/object/public/store-resources/9149c32d-950e-46a4-9f6d-96ee13602bda/1793775115043_vytvarka-dil01-p04-b10624fe.webp",
    "label": "Roční plán · Strana 4",
    "title": "Roční plán pro 1.–3. ročník",
    "text": "Pět bloků s tématem, cílem, technikou a umělcem."
  },
  {
    "src": "https://aekpgsbqjzqntlbucbts.supabase.co/storage/v1/object/public/store-resources/9149c32d-950e-46a4-9f6d-96ee13602bda/1793775137926_vytvarka-dil01-p35-4a7be411.webp",
    "label": "Bezpečnost · Strana 35",
    "title": "Bezpečnost a úklid",
    "text": "Jasná pravidla k vyvěšení ve výtvarné učebně."
  }
];
export const pageTypes = ['Hotové hodiny', 'Pracovní listy', 'Techniky a projekty', 'Hodnocení'];
export const bonuses: Array<{
  title: string;
  text: string;
  items: string[];
  benefit: string;
}> = [{
  title: 'Suplování bez stresu – 12 hodin výtvarky s minimem pomůcek',
  text: 'Třída tvoří dál, i když vy chybíte.',
  items: ['Předávací list', '12 hotových hodin', '4 pro každou skupinu ročníků', 'Jednoduché pomůcky'],
  benefit: 'Odejdete s klidnou hlavou: hodina proběhne.'
}, {
  title: 'Rychlý start – 30 tvořivých rozcviček na 5 minut',
  text: '30 rozcviček na 5 minut, se kterými nastartujete každou hodinu ve skicáku.',
  items: ['Rychlá kresba', 'Hry s barvou', 'Tvary a linie', 'Dívání se na dílo'],
  benefit: 'Hodina nastartovaná za pět minut.'
}, {
  title: 'Plakáty do výtvarné učebny – 15 plakátů k tisku',
  text: 'Barevné plakáty o barvách, technikách a pravidlech výtvarné učebny.',
  items: ['Barevný kruh', 'Plány a výřezy', 'Postup projektu', 'Pravidla a úklid'],
  benefit: 'Učebna, která učí, i když zrovna mluvíte vy.'
}, {
  title: 'Výtvarka s malým rozpočtem – tvoříme z odpadového materiálu',
  text: '25 krásných a odvážných projektů z odpadového materiálu.',
  items: ['Jak zorganizovat sběr', 'Co schovávat', '25 projektů podle ročníků', 'Vlastní nástroje'],
  benefit: 'Velké projekty skoro bez rozpočtu.'
}, {
  title: 'Závěrečná výstava prací krok za krokem',
  text: 'Vše pro uspořádání krásné výstavy prací vašich žáků.',
  items: ['Plán na 8 týdnů', 'Popisky k tisku', 'Role žáků', 'Pozvánka pro rodiče'],
  benefit: 'Vydařená výstava bez stresu.'
}];
export const steps: Array<{
  title: string;
  text: string;
}> = [{
  title: 'Otevřete VÝTVARKU HOTOVOU™',
  text: 'Stáhněte si balíček a otevřete díl, který odpovídá vaší třídě.'
}, {
  title: 'Vyberte hodinu',
  text: 'Najděte blok a téma a vyberte hodinu, techniku nebo projekt, který se hodí pro vaše žáky.'
}, {
  title: 'Hotovo za 5 minut',
  text: 'Přečtěte si průběh, zkontrolujte pomůcky a napište motivaci na tabuli.'
}, {
  title: 'Vytisknout a do třídy',
  text: 'Vytiskněte pracovní list nebo ho promítněte – a začněte s jasným plánem.'
}];
export const audienceItems = ['Učíte výtvarnou výchovu na 1. stupni ZŠ (1.–5. ročník) bez aprobace', 'Jste učitel nebo učitelka výtvarné výchovy na 2. stupni ZŠ (6.–9. ročník)', 'Chcete mít celý školní rok výtvarné výchovy zorganizovaný', 'Nechcete každou hodinu připravovat od nuly', 'Máte v jedné třídě různé úrovně', 'Hledáte techniky vysvětlené krok za krokem', 'Chcete hodnotit podle výstupů a netrávit nad tím večery', 'Chcete se na každou hodinu cítit připravení'];
export const timeValueTasks = ['Hledání nápadů na hodiny', 'Sepisování průběhu a zadání', 'Tvorba pracovních listů', 'Příprava dějin umění', 'Hodnocení a psaní slovních hodnocení'];
export const offerRows: Array<{
  label: string;
  detail: string;
}> = [{
  label: '6 digitálních dílů – 240 stran',
  detail: 'Roční plán, hodiny, techniky, prostorová tvorba, obraz, dějiny umění a hodnocení'
}, {
  label: '5 bonusů bez příplatku',
  detail: 'Suplování, rozcvičky, plakáty, malý rozpočet a výstava'
}, {
  label: 'Od 1. do 9. ročníku',
  detail: 'Hotové hodiny, listy k tisku a přehled výstupů'
}];
export const offerIncludes = ['Okamžitě ke stažení', 'Soubory PDF k uložení', 'Pracovní listy k tisku', 'Nezasíláme žádný fyzický produkt'];
export const PRICE = '490 Kč';
export const COMPARE_PRICE = '1 990 Kč';

// Only add authentic reviews of VÝTVARKA HOTOVÁ. The section stays hidden while empty.
export const testimonials: Array<{
  quote: string;
  name: string;
  location: string;
}> = [];
export const faqs = [{
  q: 'Jak materiál obdržím?',
  a: 'VÝTVARKA HOTOVÁ™ je digitální produkt. Po nákupu získáte přístup k souborům PDF, které si můžete stáhnout a uložit. Používáte je na počítači, tabletu nebo telefonu a vytisknete si listy, které potřebujete.'
}, {
  q: 'Odpovídá materiál RVP ZV?',
  a: 'Ano. Materiál vychází z RVP ZV, ze vzdělávací oblasti Umění a kultura a oboru Výtvarná výchova, a pracuje s jeho očekávanými výstupy a se setkáním s uměním. Jde o nezávislý výukový materiál – tento materiál není oficiální publikací MŠMT.'
}, {
  q: 'Hodí se od 1. do 9. ročníku?',
  a: 'Ano. Šest dílů je uspořádaných podle tří skupin ročníků: 1.–3. ročník (1. období), 4.–6. ročník (2. období a 6. ročník) a 7.–9. ročník (2. stupeň). Každý pracovní list má 3 úrovně.'
}, {
  q: 'Musím umět kreslit?',
  a: 'Ne. Každá technika je vysvětlená krok za krokem, s ilustracemi a slovy, která můžete říct. Balíček je vytvořený i pro učitele 1. stupně, kteří nemají aprobaci na výtvarnou výchovu.'
}, {
  q: 'Opravdu připravím hodinu za 5 minut?',
  a: 'VÝTVARKA HOTOVÁ™ je vytvořená tak, aby vaši přípravu zrychlila. Otevřete díl, vyberete hotovou hodinu a projdete ji přibližně za pět minut. Čas se může lišit podle třídy a úprav, které uděláte.'
}, {
  q: 'Budu mít dost materiálu na celý školní rok?',
  a: 'VÝTVARKA HOTOVÁ™ obsahuje hodiny a projekty na celý školní rok, od září po červnovou výstavu. Vybíráte, kombinujete a přizpůsobujete podle svého ŠVP a své třídy.'
}, {
  q: 'Potřebuji hodně pomůcek?',
  a: 'Ne. Hodiny používají běžné pomůcky (pastelky, fixy, tempery, papír, karton) a každý projekt uvádí jednoduchou variantu. Bonus „Výtvarka s malým rozpočtem“ nabízí 25 projektů z odpadového materiálu.'
}, {
  q: 'A bez tabletu?',
  a: 'Každý digitální projekt v dílu 04 má papírovou variantu: kresbu, koláž, flipbook nebo pixel art na čtverečkovaném papíře.'
}, {
  q: 'Můžu ho používat ve třídě s různými úrovněmi?',
  a: 'Ano. Každý list má 3 úrovně, takže celá třída pracuje se stejnou motivací, každý na své úrovni.'
}, {
  q: 'Je všech pět bonusů v ceně?',
  a: `Ano. Všech pět bonusů je v balíčku bez příplatku – vše dohromady za ${PRICE}.`
}, {
  q: 'Je to předplatné?',
  a: 'Ne. Zaplatíte jednou a získáte přístup k digitálnímu materiálu. Bez opakovaných plateb.'
}, {
  q: 'Můžu si soubory uložit?',
  a: 'Ano. Soubory PDF si můžete stáhnout a uložit v souladu s podmínkami užívání produktu.'
}, {
  q: 'Můžu ho používat na telefonu nebo tabletu?',
  a: 'Ano. PDF otevřete na počítači, tabletu i telefonu. Pro kontrolu rozvržení před tiskem doporučujeme počítač nebo tablet.'
}, {
  q: 'Je k dispozici záruka?',
  a: 'Ano. VÝTVARKA HOTOVÁ™ má 7denní záruku spokojenosti. Pokud materiál nesplní vaše očekávání, pošlete nám během záruční lhůty e-mail a požádejte o vrácení peněz, v souladu s podmínkami.'
}];
export const footerLinks = ['Obchodní podmínky', 'Ochrana osobních údajů', 'Vrácení peněz', 'Kontakt'];
